import { ApiClient } from '../lib/api';
import { z } from 'zod';
import { 
    EagleViewReport, 
    EagleViewReportSchema, 
    EagleViewProduct, 
    EagleViewProductSchema,
    PriceOrderRequest,
    PriceOrderResponse,
    PriceOrderResponseSchema,
    PlaceOrderRequest,
    PlaceOrderResponse,
    PlaceOrderResponseSchema,
    AccountDetailsResponse,
    AccountDetailsResponseSchema,
    SolarProductAvailabilityRequest,
    SolarProductAvailabilityResponse,
    SolarProductAvailabilityResponseSchema,
    ReportUpgradeProductsResponse,
    ReportUpgradeProductsResponseSchema,
    PlaceUpgradeOrderRequest,
    GetReportsRequest,
    GetReportsResponse,
    GetReportsResponseSchema,
    StructureWasteMeasurementsResponse,
    StructureWasteMeasurementsResponseSchema,
    UpdateReportAddressRequest,
    UpdateReportAddressResponse,
    UpdateReportAddressResponseSchema,
    FileLinksResponse,
    FileLinksResponseSchema
} from '../models/eagleview';

export class EagleViewService {
    private api: ApiClient;
    private eagleViewAt: string | undefined;
    private eagleViewRt: string | undefined;

    constructor(baseURL: string, stage: string, eagleViewAt?: string, eagleViewRt?: string) {
        // this.api = new ApiClient(`${baseURL}/${stage}/eagleview-service-${stage}`);
        this.api = new ApiClient('https://sandbox.apicenter.eagleview.com', stage);
        this.eagleViewAt = eagleViewAt;
        this.eagleViewRt = eagleViewRt;

        this.api.setupInterceptors(this.eagleViewAt, this.eagleViewRt);
    }

    private setDefaults<T>(data: unknown, schema: z.ZodSchema<T>): T {
        return schema.parse(data);
    }

    // Get available products
    async getAvailableProducts(): Promise<EagleViewProduct[]> {
        const response = await fetch(
            `https://sandbox.apicenter.eagleview.com/v2/Product/GetAvailableProducts`,
            // { method: 'GET', url: `/products?companyId=${companyId}` },
            { method: 'GET', headers: { 'Authorization': `Bearer ${this.eagleViewAt}` } },
            // EagleViewProductSchema.array()
        );
        if (!response.ok) {
            throw new Error('Failed to fetch products');
        }
        const products = await response.json() as EagleViewProduct[];
        return products.map(product => this.setDefaults<EagleViewProduct>(product, EagleViewProductSchema));
    }

    // Price an order
    async priceOrder(orderData: PriceOrderRequest): Promise<PriceOrderResponse> {
        const response = await fetch(
            `https://sandbox.apicenter.eagleview.com/v2/Order/PriceOrder`,
            { 
                method: 'POST', 
                headers: { 
                    'Authorization': `Bearer ${this.eagleViewAt}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(orderData)
            }
        );
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to place order: ${response.status} ${errorText}`);
        }
        const pricing = await response.json() as PriceOrderResponse;
        return this.setDefaults<PriceOrderResponse>(pricing, PriceOrderResponseSchema);
    }

    // Place an order
    async placeOrder(orderData: PlaceOrderRequest): Promise<PlaceOrderResponse> {
        const response = await fetch(
            `https://sandbox.apicenter.eagleview.com/v2/Order/PlaceOrder`,
            { 
                method: 'POST', 
                headers: { 
                    'Authorization': `Bearer ${this.eagleViewAt}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(orderData)
            }
        );
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to place order: ${response.status} ${errorText}`);
        }
        const order = await response.json() as PlaceOrderResponse;
        return this.setDefaults<PlaceOrderResponse>(order, PlaceOrderResponseSchema);
    }

    async getReport(reportId: number): Promise<EagleViewReport> {
        const response = await fetch(
            `https://sandbox.apicenter.eagleview.com/v2/Report/GetReport?reportId=${reportId}`,
            { 
                headers: { 'Authorization': `Bearer ${this.eagleViewAt}` }
            }
        );
        if (!response.ok) {
            throw new Error('Failed to fetch report');
        }
        const report = await response.json() as EagleViewReport;
        return this.setDefaults<EagleViewReport>(report, EagleViewReportSchema);
    }

    // Get account details
    async getAccountDetails(): Promise<AccountDetailsResponse> {
        // const account = await this.api.request(
        //     { method: 'GET', url: `/account?companyId=${companyId}` },
        //     AccountDetailsResponseSchema
        // );
        const response = await fetch(
            `https://sandbox.apicenter.eagleview.com/v3/Order/GetAccountDetails`,
            { 
                headers: { 'Authorization': `Bearer ${this.eagleViewAt}` }
            }
        );
        if (!response.ok) {
            throw new Error('Failed to place order');
        }
        const account = await response.json() as AccountDetailsResponse;
        return this.setDefaults<AccountDetailsResponse>(account, AccountDetailsResponseSchema);
    }

    // Check solar product availability
    async checkSolarProductAvailability(companyId: number, availabilityData: SolarProductAvailabilityRequest): Promise<SolarProductAvailabilityResponse> {
        const availability = await this.api.request(
            { 
                method: 'POST', 
                url: `/products/solar-availability?companyId=${companyId}`,
                data: availabilityData 
            },
            SolarProductAvailabilityResponseSchema
        );
        return this.setDefaults<SolarProductAvailabilityResponse>(availability, SolarProductAvailabilityResponseSchema);
    }

    // Get report upgrade products
    async getReportUpgradeProducts(companyId: number, reportId: number): Promise<ReportUpgradeProductsResponse> {
        const upgradeProducts = await this.api.request(
            { method: 'GET', url: `/reports/upgrade-products?companyId=${companyId}&reportId=${reportId}` },
            ReportUpgradeProductsResponseSchema
        );
        return this.setDefaults<ReportUpgradeProductsResponse>(upgradeProducts, ReportUpgradeProductsResponseSchema);
    }

    // Place an upgrade order
    async placeUpgradeOrder(companyId: number, upgradeData: PlaceUpgradeOrderRequest): Promise<number> {
        const upgrade = await this.api.request(
            { 
                method: 'POST', 
                url: `/orders/upgrade?companyId=${companyId}`,
                data: upgradeData 
            },
            z.number()
        );
        return upgrade;
    }

    // // Get report details
    // async getReport(reportId: number, companyId: number): Promise<EagleViewReport> {
    //     const report = await this.api.request(
    //         { method: 'GET', url: `/reports/${reportId}?companyId=${companyId}` },
    //         EagleViewReportSchema
    //     );
    //     return this.setDefaults<EagleViewReport>(report, EagleViewReportSchema);
    // }

    // Get reports list
    async getReports(companyId: number, page: number = 1, count: number = 10, filters: GetReportsRequest): Promise<GetReportsResponse> {
        const reports = await this.api.request(
            { 
                method: 'POST', 
                url: `/reports?companyId=${companyId}&page=${page}&count=${count}`,
                data: filters 
            },
            GetReportsResponseSchema
        );
        return this.setDefaults<GetReportsResponse>(reports, GetReportsResponseSchema);
    }

    // Get structure waste measurements
    async getStructureWasteMeasurements(reportId: number, companyId: number): Promise<StructureWasteMeasurementsResponse> {
        const wasteMeasurements = await this.api.request(
            { method: 'GET', url: `/reports/${reportId}/structure-waste-measurements?companyId=${companyId}` },
            StructureWasteMeasurementsResponseSchema
        );
        return this.setDefaults<StructureWasteMeasurementsResponse>(wasteMeasurements, StructureWasteMeasurementsResponseSchema);
    }

    // Update report address
    async updateReportAddress(reportId: number, companyId: number, addressData: UpdateReportAddressRequest): Promise<UpdateReportAddressResponse> {
        const address = await this.api.request(
            { 
                method: 'POST', 
                url: `/reports/${reportId}/address?companyId=${companyId}`,
                data: addressData 
            },
            UpdateReportAddressResponseSchema
        );
        return this.setDefaults<UpdateReportAddressResponse>(address, UpdateReportAddressResponseSchema);
    }

    // Approve report type change
    async approveReportTypeChange(reportId: number, companyId: number): Promise<string> {
        const approval = await this.api.request(
            { method: 'POST', url: `/reports/${reportId}/approve-type-change?companyId=${companyId}` },
            z.string()
        );
        return approval;
    }

    // Deny report type change
    async denyReportTypeChange(reportId: number, companyId: number): Promise<string> {
        const denial = await this.api.request(
            { method: 'POST', url: `/reports/${reportId}/deny-type-change?companyId=${companyId}` },
            z.string()
        );
        return denial;
    }

    // Close report
    async closeReport(reportId: number, companyId: number): Promise<string> {
        const closure = await this.api.request(
            { method: 'POST', url: `/reports/${reportId}/close?companyId=${companyId}` },
            z.string()
        );
        return closure;
    }

    // Get report file
    async getReportFile(reportId: number): Promise<unknown> {

        const response = await fetch(
            `https://sandbox.apicenter.eagleview.com/v1/File/GetReportFile?fileFormat=18&fileType=107&reportId=${reportId}`,
            { 
                headers: { 'Authorization': `Bearer ${this.eagleViewAt}` }
            }
        );
        if (!response.ok) {
            throw new Error('Failed to fetch report');
        }
        const blob = await response.blob();
        const text = await blob.text();
        const report = JSON.parse(text);
        return report;
    }

    // Get report file in any format
    async getReportFileAnyFormat(reportId: number, companyId: number, fileType?: number): Promise<Blob> {
        let url = `/reports/${reportId}/file-any-format?companyId=${companyId}`;
        if (fileType !== undefined) url += `&fileType=${fileType}`;
        
        const response = await this.api.request(
            { 
                method: 'GET', 
                url,
                responseType: 'blob' 
            },
            z.instanceof(Blob)
        );
        return response as Blob;
    }

    // Get file download links
    async getFileLinks(reportId: number, companyId: number): Promise<FileLinksResponse> {
        const links = await this.api.request(
            { method: 'GET', url: `/reports/${reportId}/file-links?companyId=${companyId}` },
            FileLinksResponseSchema
        );
        return this.setDefaults<FileLinksResponse>(links, FileLinksResponseSchema);
    }
} 